import styles from './DocumentCreationItem.module.css'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'

import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownScroll,
} from '@/components/Dropdown'
import { MiddleEllipsis } from '@/components/MiddleEllipsis'
import { SearchField } from '@/components/SearchField'
import type { Id } from '@/infra/id'

export interface DocumentCreationItemType {
  id: Id
  name: string
  selected: boolean
}

export interface DocumentCreationItemProps {
  index: number
  fileName: string
  typeName: string
  isDropdownOpen: boolean
  onToggleDropdown: () => unknown
  onCloseDropdown: () => unknown
  searchQuery: string
  onSearchQueryChange: (query: string) => unknown
  types: DocumentCreationItemType[]
  /**
   * The catch-all "Other" type, listed last; `null` when unavailable.
   */
  otherType: DocumentCreationItemType | null
  hasNoMatchingTypes: boolean
  onSelectType: (id: Id) => unknown
  onRemove: (index: number) => void
}

function getDocumentTypeDropdownTestId(typeId: Id, fileName: string) {
  switch (typeId) {
    case 'FINAL_RETURN':
      return `upload-final-documents-file-document-type-dropdown-final-return-item-${fileName}`
    case 'PREP_NOTES':
      return `upload-final-documents-file-document-type-dropdown-prep-notes-item-${fileName}`
    case 'REVIEW_NOTES':
      return `upload-final-documents-file-document-type-dropdown-review-notes-item-${fileName}`
    case 'EXTENSION_CONFIRMATION':
      return `upload-final-documents-file-document-type-dropdown-extension-confirmation-item-${fileName}`
    case 'DRAFT_RETURN':
      return `upload-draft-documents-file-document-type-dropdown-${fileName}`
  }

  return undefined
}

function DocumentTypeDropdown({
  fileName,
  typeName,
  isDropdownOpen,
  onToggleDropdown,
  onCloseDropdown,
  searchQuery,
  onSearchQueryChange,
  types: typesWithoutOther,
  otherType,
  hasNoMatchingTypes,
  onSelectType,
}: Omit<DocumentCreationItemProps, 'index' | 'onRemove'>) {
  const handleSelect = (type: DocumentCreationItemType) => {
    onSelectType(type.id)
  }

  return (
    <Dropdown
      isOpen={isDropdownOpen}
      onButtonPress={() => onToggleDropdown()}
      onRequestClose={() => onCloseDropdown()}
      minWidth={400}
      position="bottom-end"
      button={
        <Button
          label={typeName}
          className={styles.documentTypeButton}
          borderless
          size="normal"
          iconRight="dropdown"
          iconSize={24}
          testId={`upload-final-documents-file-document-type-dropdown-${fileName}`}
        />
      }
    >
      <div className={styles.typeSearchContainer}>
        <SearchField
          size="small"
          stretch
          placeholder="Search document types..."
          value={searchQuery}
          onChange={onSearchQueryChange}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <DropdownScroll>
        {typesWithoutOther.map((type, index) => (
          <DropdownItem
            key={index}
            onPress={() => handleSelect(type)}
            active={type.selected}
            checkmark={type.selected}
            buttonTestId={getDocumentTypeDropdownTestId(type.id, fileName)}
          >
            <div className={styles.documentType}>
              <DropdownItemTitle className={styles.documentTypeTitle}>
                {type.name}
              </DropdownItemTitle>
            </div>
          </DropdownItem>
        ))}
        {!searchQuery.trim() && otherType && (
          <DropdownItem
            onPress={() => handleSelect(otherType)}
            active={otherType.selected}
            checkmark={otherType.selected}
          >
            <div className={styles.documentType}>
              <DropdownItemTitle className={styles.documentTypeTitle}>
                Other documents
              </DropdownItemTitle>
            </div>
          </DropdownItem>
        )}
        {hasNoMatchingTypes && (
          <div className={styles.noResults}>No matching types</div>
        )}
      </DropdownScroll>
    </Dropdown>
  )
}

export function DocumentCreationItem({
  index,
  onRemove,
  ...dropdownProps
}: DocumentCreationItemProps) {
  return (
    <div className={styles.creationItem}>
      <div className={styles.creationActions}>
        <IconButton
          name="close"
          label="Remove file"
          className={styles.removeButton}
          onPress={() => onRemove(index)}
        />
      </div>
      <div className={styles.creationInfo} title={dropdownProps.fileName}>
        <MiddleEllipsis>
          <span className={styles.creationTitle}>{dropdownProps.fileName}</span>
        </MiddleEllipsis>
      </div>
      <div>
        <DocumentTypeDropdown {...dropdownProps} />
      </div>
    </div>
  )
}
